import { Component, ElementRef, AfterViewInit, inject, Signal, computed, ViewEncapsulation, WritableSignal, signal, InputSignal, input } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { GeneralHelpDTO, GeneralHelpGroupDTO, GeneralHelpGroupContentsDTO } from '../help.dtos';
import { UIService } from '../../../../ui.service';
import { PlycaDataService } from '../../../../../services/plyca.service';
import { TreeNode } from 'primeng/api';
import { Configuration } from '../../../../conf/app.constants';
import { forkJoin } from 'rxjs';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { HelpService } from '../help.service';
import { HeaderActionsService } from '../../sub-header/header-actions.service';
import { UserService } from '../../../../user.service';
import { HelpDeskDTO } from '../../../../api/plyca-dtos-v2';
import { filter, finalize } from 'rxjs/operators';
import { WaitLoadingService } from '../../waitLoading/wait-loading.service';
import { BaseComponent } from '../../baseComponent/base.component';
import { HelpElementComponent } from '../help-element/help-element.component';
import { TreeModule } from 'primeng/tree';
import { NgIf, NgFor, NgTemplateOutlet, NgClass } from '@angular/common';
import { ActivationStart, Router, RouterModule } from '@angular/router';
import { RoutingPaths } from '@/app/common/routing';
import { ScreenService } from '@/services/screen/screen.service';
import { AccordionModule } from 'primeng/accordion';
import { PlycaUiButtonGenericComponent } from '@plyca/ui-library/components/atoms/buttons/button-generic/button-generic.component';
import { goToFragmentHelp } from './config/general-help.functions';
import { AnchorLinkDirective } from '@/app/_presentation/features/directives/click/anchor-link.directive';
import { HelpUseCase } from '@/app/_core/application/help/help.use-cases';
import { GeneralHelpConfig } from './config/general-help.model';
import { GeneralHelpModeConst } from './config/general-help.const';
import { HttpUtils } from '@/app/_core/domain/http/http-utils/http-utils';
import { API_KEY } from '@/app/_core/domain/http/http-utils/apis-url';
import { CONFIG_JSON_KEYS } from '@/app/_core/domain/const/config-json-keys.const';
import { AppConfiguration } from '@/app/conf/app.configuration';
import { LanguageService } from '@/services/language.service';
import { HelpType } from '../_config/help-type.const';
import { effect } from '@angular/core';

@Component({
  selector: 'plyca-general-help',
  templateUrl: 'general-help.component.html',
  styleUrls: ['general-help.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [NgIf, NgFor, NgTemplateOutlet, NgClass, TreeModule, HelpElementComponent, TranslateModule, RouterModule, AccordionModule, PlycaUiButtonGenericComponent, AnchorLinkDirective]
})
export class GeneralHelpComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly _deviceDetectorService = inject(ScreenService);
  private readonly _languageService = inject(LanguageService);
  private readonly _appConfig = inject(AppConfiguration);
  private readonly _helpUseCases = inject(HelpUseCase);
  private readonly _httpUtils = inject(HttpUtils);
  private readonly router = inject(Router);

  public config: InputSignal<GeneralHelpConfig> = input.required<GeneralHelpConfig>();
  private _headerActionsSet = false;
  private _isSelectingFromTree = false;

  public isGeneralHelpRoute: Signal<boolean> = computed(() => this.router.url.includes(RoutingPaths.LoggedInGeneralHelp));
  public readonly isDesktop: Signal<boolean> = computed(() => this._deviceDetectorService.isDesktopSig());
  public readonly isLoadedSig: WritableSignal<boolean> = signal(false);

  public readonly effectiveMode = computed<string>(() => {
    if (this.config()?.mode) return this.config().mode;
    return this.isDesktop() && !this.isGeneralHelpRoute() ? GeneralHelpModeConst.ACCORDION : GeneralHelpModeConst.PLAIN;
  });

  public helpContent: any;
  public selectedHelp: any;
  public locale: string;
  public helperOptions: Array<HelpDeskDTO>;
  public accordionIndex: number | number[] | null = 0;
  public selectHelpMode = GeneralHelpModeConst;

  constructor(
    public uiService: UIService,
    public plycaService: PlycaDataService,
    public userService: UserService,
    public helpService: HelpService,
    private headerActionsService: HeaderActionsService,
    public configuration: Configuration,
    private elem: ElementRef,
    private waitService: WaitLoadingService,
    public translate: TranslateService
  ) {
    super();
    this.locale = this.translate.currentLang;
    this._controlRoute();
    this._initEffects();
  }

  private _initEffects(): void {
    effect(() => {
      if (this.config()?.isSidebar) return;
      const helpState = this._helpUseCases.selectHelpSig();
      if (helpState.isGeneralHelp) {
        this._restoreSelection(helpState.selectedGeneralHelpNodeId);
      }
    });
  }

  private _restoreSelection(nodeLabel: string | undefined | null): void {
    if (!this.helpContent) return;
    if (this._isSelectingFromTree) return;
    if (!nodeLabel) {
      if (this.selectedHelp !== this.helpContent[0]) {
        this.selectedHelp = this.helpContent[0] || null;
      }
      return;
    }
    this._expandParentNodes(this.helpContent, nodeLabel);
    const foundNode = this._findNodeByLabel(this.helpContent, nodeLabel);
    if (foundNode && this.selectedHelp !== foundNode) {
      this.selectedHelp = foundNode;
    }
  }

  private _findNodeByLabel(nodes: TreeNode[], label: string): TreeNode | null {
    for (const node of nodes) {
      if (node.label === label) return node;
      if (node.children) {
        const found = this._findNodeByLabel(node.children, label);
        if (found) return found;
      }
    }
    return null;
  }

  private _expandParentNodes(nodes: TreeNode[], targetLabel: string): boolean {
    for (const node of nodes) {
      if (node.label === targetLabel) return true;
      if (node.children && node.children.length) {
        const found = this._expandParentNodes(node.children, targetLabel);
        if (found) {
          node.expanded = true;
          return true;
        }
      }
    }
    return false;
  }

  ngOnInit(): void {
    this.waitService.startWaiting();
    this._loadData();
  }

  ngAfterViewInit(): void {
    if (this.isGeneralHelpRoute() && !this.config()?.isSidebar) {
      this._headerActionsSet = true;
      this.headerActionsService.setTaskDescription(this.elem, 'General.ayuda', 'fas fa-question', true);
      this.headerActionsService.addDivider();
      this.subscribe(this.headerActionsService.addAction('General.back', 'fas fa-chevron-left', 'btn-outline-secondary', true), (success) => {
        this.uiService.routeBack();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.isDesktop() && this.isGeneralHelpRoute() && !this.config()?.isSidebar) {
      this._helpUseCases.setHelp({ context: null, isOpenHelp: false, isGeneralHelp: false });
      this.uiService = null;
    }
    if (this._headerActionsSet) {
      this.headerActionsService.clearActions();
    }
    super.ngOnDestroy();
  }

  private _controlRoute(): void {
    this.subscribe(this.router.events.pipe(filter((event) => event instanceof ActivationStart)), (event: ActivationStart) => {
      this.isGeneralHelpRoute = computed(() => event.snapshot.routeConfig?.path?.includes(RoutingPaths.LoggedInGeneralHelp));
    });
  }

  private _loadData(): void {
    if (this.isGeneralHelpRoute() || (this._helpUseCases.selectHelpSig().isGeneralHelp && !this._helpUseCases.selectHelpSig()?.extraData?.isRefreshCounter)) {
      this.waitService.startWaiting();
      const timeCacheRequest = this._appConfig.getValue(CONFIG_JSON_KEYS.timeCacheRequests);
      const callOne = this._httpUtils.get(API_KEY.GET_GENERAL_HELP, { locale: this.locale }, false, timeCacheRequest);
      const callTwo = this._httpUtils.get(API_KEY.GET_GENERAL_HELP_OPTION, { lang: this._languageService.getAppLanguage() }, false, timeCacheRequest);
      this.subscribe(
        forkJoin([callOne, callTwo]).pipe(
          finalize(() => {
            this.isLoadedSig.set(true);
            this.waitService.cancelWaiting();
          })
        ),
        (result: any[]) => {
          try {
            if (result[0] && result[0].content) {
              // this.dto2TreeModel(result[0]);
              this.dto2TreeModel(result[0]);
              // Expandir el primer elemento
              if (!this.helpContent || !this.helpContent[0] || !this.helpContent[0].expanded) {
                this.helpContent[0].expanded = true;
              }
              this.selectedHelp = this.helpContent && this.helpContent[0] ? this.helpContent[0] : null;
              this.selectedHelp = this.helpContent && this.helpContent[0] && this.helpContent[0].children && this.helpContent[0].children[0] ? this.helpContent[0] : null;

              const currentHelp = this._helpUseCases.selectHelpSig();
              const selectedHelpLabel = this.selectedHelp?.label;

              if (this.isGeneralHelpRoute() && !this.config()?.isSidebar) {
                // Al entrar en la vista de página, siempre empezamos con historial limpio
                // para que no aparezca contexto de ayuda de vistas anteriores
                this._helpUseCases.setHelp({
                  ...currentHelp,
                  isGeneralHelp: true,
                  currentTag: HelpType.GENERAL_HELP,
                  tagByActualView: HelpType.GENERAL_HELP,
                  selectedGeneralHelpNodeId: selectedHelpLabel,
                  history: [{ tags: [HelpType.GENERAL_HELP], data: [], selectedNodeId: selectedHelpLabel }],
                  future: []
                });
              } else if (this.config()?.isSidebar) {
                this._helpUseCases.setHelp({
                  ...currentHelp,
                  isGeneralHelp: true,
                  currentTag: HelpType.GENERAL_HELP,
                  tagByActualView: HelpType.GENERAL_HELP,
                  selectedGeneralHelpNodeId: selectedHelpLabel,
                  history: [{ tags: [HelpType.GENERAL_HELP], data: [], selectedNodeId: selectedHelpLabel }],
                  future: []
                });
              }
            }
          } catch (e) {
            console.log(e);
          }
          try {
            if (result[1]) {
              this.helperOptions = result[1];
            }
          } catch (e) {
            console.log(e);
          }
        }
      );
    }
  }

  group2Node(group: GeneralHelpGroupContentsDTO, label?: string): TreeNode {
    return {
      label: label ? label : 'Plyca-Expedientes',
      data: group,
      expandedIcon: 'far fa-folder-open',
      collapsedIcon: 'far fa-folder',
      expanded: false,
      children: this.groupElements2Array(group.content)
    };
  }

  subGroup2Node(label: string): TreeNode {
    return {
      label: label,
      data: { content: [] },
      expandedIcon: 'far fa-folder-open',
      collapsedIcon: 'far fa-folder',
      expanded: false,
      children: []
    };
  }

  element2Node(element: GeneralHelpGroupDTO) {
    const filteredElements = element.content.filter((hl) => !hl.profiles || hl.profiles.length === 0 || this.userService.hasRoles(hl.profiles.join(', ')));
    element.content = filteredElements;
    return {
      label: element.name,
      data: element,
      expandedIcon: 'far fa-folder-open',
      collapsedIcon: 'fas fa-file-alt'
    };
  }

  groupElements2Array(elements: Array<GeneralHelpGroupDTO>): Array<TreeNode> {
    const resp: Array<TreeNode> = [];
    if (elements) {
      elements.forEach((el) => {
        resp.push(this.element2Node(el));
      });
    }
    return resp;
  }

  cleanGroupPath(node: TreeNode) {
    if (node.label.includes('>')) {
      node.label = node.label.substring(node.label.lastIndexOf('>') + 1);
    }
    if (node.data?.description && node.data.description.includes('>')) {
      node.data.description = node.data.description.substring(node.data.description.lastIndexOf('>') + 1);
    }
    if (node.data?.name && node.data.name.includes('>')) {
      node.data.name = node.data.name.substring(node.data.name.lastIndexOf('>') + 1);
    }
    if (node.children) {
      node.children.forEach((child: TreeNode) => {
        this.cleanGroupPath(child);
      });
    }
  }

  searchTreeNode(node: TreeNode, searchTerm: string): TreeNode | null {
    if (node.label === searchTerm) {
      return node;
    }

    if (node.children) {
      for (const child of node.children) {
        const result = this.searchTreeNode(child, searchTerm);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }

  addContent2Group(group: GeneralHelpGroupDTO, node: TreeNode) {
    const g: TreeNode = this.element2Node(group);
    // Se eliminan los grupos sin contenido
    if (g.data && g.data.content && g.data.content.length > 0) {
      node.children.push(g);
      node.data.content.push(group.content);
    }
  }

  dto2TreeModel(dto: GeneralHelpDTO) {
    const _helpContent: TreeNode[] = [];
    _helpContent['data'] = [];
    const levelGroups: string[] = [];
    dto.content.forEach((group: GeneralHelpGroupContentsDTO) => {
      group.content.sort((a, b) =>
        //Ordenamos primero por orderStr y despues por nombre
        this.uiService.normalizeString(a.orderStr ? a.orderStr : 'ZZZZ') > this.uiService.normalizeString(b.orderStr ? b.orderStr : 'ZZZZ')
          ? 1
          : this.uiService.normalizeString(b.orderStr ? b.orderStr : 'ZZZZ') > this.uiService.normalizeString(a.orderStr ? a.orderStr : 'ZZZZ')
            ? -1
            : this.uiService.normalizeString(a.name) > this.uiService.normalizeString(b.name)
              ? 1
              : this.uiService.normalizeString(b.name) > this.uiService.normalizeString(a.name)
                ? -1
                : 0
      );
      group.content.forEach((entry: GeneralHelpGroupDTO) => {
        entry.content.sort((a, b) =>
          //Ordenacion entradas dentro de los grupos
          this.uiService.normalizeString(a.orderStr ? a.orderStr : 'ZZZZ') > this.uiService.normalizeString(b.orderStr ? b.orderStr : 'ZZZZ')
            ? 1
            : this.uiService.normalizeString(b.orderStr ? b.orderStr : 'ZZZZ') > this.uiService.normalizeString(a.orderStr ? a.orderStr : 'ZZZZ')
              ? -1
              : this.uiService.normalizeString(a.name) > this.uiService.normalizeString(b.name)
                ? 1
                : this.uiService.normalizeString(b.name) > this.uiService.normalizeString(a.name)
                  ? -1
                  : 0
        );
        if (entry.name.includes('>') && !entry['ignore']) {
          const groupLabel: string = entry.name.substring(0, entry.name.indexOf('>'));
          const SubGroupLabel: string = entry.name.substring(groupLabel.length + 1);
          let upperLevelNode: TreeNode = _helpContent.filter((e) => e.label == groupLabel)[0];
          if (!upperLevelNode) {
            upperLevelNode = this.subGroup2Node(groupLabel);
            _helpContent.push(upperLevelNode);
          }
          if (SubGroupLabel.includes('>')) {
            const subGroupsLabels: string[] = SubGroupLabel.split('>');
            //Iteramos entre los subgrupos
            for (const aGroupLabel of subGroupsLabels.slice(0, -1)) {
              const foundNode = this.searchTreeNode(upperLevelNode, aGroupLabel);
              if (!foundNode) {
                const newNode = this.subGroup2Node(aGroupLabel);
                upperLevelNode.children.push(newNode);
                upperLevelNode = newNode;
              } else {
                upperLevelNode = foundNode;
              }
            }
          }
          if (levelGroups.filter((e) => e == entry.name).length == 0) {
            levelGroups.push(entry.name);
            this.addContent2Group(entry, upperLevelNode);
          }
        } else if (!entry['ignore'] && entry.name && entry.name !== '') {
          const ele = this.element2Node(entry);
          if (ele.data && ele.data.content && ele.data.content.length > 0) {
            _helpContent.push(ele);
          }
        }
      });
    });
    _helpContent.forEach((node: TreeNode) => {
      this.cleanGroupPath(node);
    });
    this.helpContent = _helpContent;
  }

  print(): void {
    let printContents, popupWin;
    printContents = document.getElementById('print-section').innerHTML;
    printContents = printContents.replaceAll('/generalHelp%23', '');
    const stylesHtml = this.getTagsHtml('style');
    const linksHtml = this.getTagsHtml('link');
    popupWin = window.open('', '_print-window', 'top=0,left=0,height=80%,width=auto');
    popupWin.document.open();
    popupWin.document.write(`
    <html>
        <head>
          <title>TITULO</title>
          ${linksHtml}
          <style>
            @media print {
              body {
              margin-top: 2cm !important;
              margin-bottom: 2cm !important;
              margin-left: 2cm !important;
              margin-right: 2cm !important;
              }
            }
          </style>
          ${stylesHtml}
        </head>
    <body onload="window.print();window.close()">
      <div class="container-fluid">
      <div class="row">
      <div class="col-12">
      <div class="ui-editor-content ql-container ql-snow ql-disabled">${printContents}</div>
      </div>
      </div>
      </div>
    </body>
    </html>`);
    popupWin.document.close();
  }

  private getTagsHtml(tagName: keyof HTMLElementTagNameMap): string {
    const htmlStr: string[] = [];
    const elements = document.getElementsByTagName(tagName);
    for (let idx = 0; idx < elements.length; idx++) {
      htmlStr.push(elements[idx].outerHTML);
    }

    return htmlStr.join('\r\n');
  }

  openUrl(url: any) {
    window.open(url, '_blank', 'fullscreen=yes');
  }

  isFolder(node: TreeNode) {
    return node['children'] && node['children'].length > 0;
  }

  isIndexNeeded(node: TreeNode) {
    return node && node.data.content.length > 1;
  }

  hasContent(node: TreeNode) {
    return node && node.data.content.length > 0;
  }

  public resetScroll(): void {
    if (this.config()?.isSidebar) {
      const container = document.getElementById('generalHelpScrollRef') || this.elem.nativeElement.closest('.layout__container-general-help');
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
      }
      this.accordionIndex = -1;
      return;
    }
    window.scrollTo(0, 0);
    if (this.isDesktop() && !this.isGeneralHelpRoute()) {
      this.accordionIndex = -1;
    }
  }

  public onGoToElement(identifier: string): void {
    if (this.config()?.isSidebar) {
      const targetEl = this.elem.nativeElement.querySelector(`[id='${identifier}']`);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    goToFragmentHelp(this.router, identifier, {
      isDesktop: this.isDesktop(),
      isGeneralRoute: this.isGeneralHelpRoute(),
      onStart: () => this.waitService.startWaiting(),
      onEnd: () => this.waitService.cancelWaiting()
    });
  }

  public onNodeSelect(event: any): void {
    const node = event.node;
    if (!node || !node.label) return;
    this.selectedHelp = node;
    this.resetScroll();

    if (this.config()?.isSidebar) return;
    const currentHelp = this._helpUseCases.selectHelpSig();
    if (currentHelp.selectedGeneralHelpNodeId === node.label) {
      return;
    }

    this.waitService.startWaiting();
    this._isSelectingFromTree = true;

    const history = [...(currentHelp.history || [])];
    const lastIdx = history.length - 1;
    if (lastIdx >= 0 && history[lastIdx].tags.toString() === HelpType.GENERAL_HELP) {
      history[lastIdx] = { ...history[lastIdx], selectedNodeId: node.label };
    } else {
      history.push({ tags: [HelpType.GENERAL_HELP], data: [], selectedNodeId: node.label });
    }

    this._helpUseCases.setHelp({
      ...currentHelp,
      isGeneralHelp: true,
      currentTag: HelpType.GENERAL_HELP,
      selectedGeneralHelpNodeId: node.label,
      history: history
    });

    // Simulate a small delay to ensure the content is rendered before removing the spinner
    setTimeout(() => {
      this._isSelectingFromTree = false;
      this.waitService.cancelWaiting();
    }, 500);
  }
}
